import { useState } from 'react'
import { motion } from 'framer-motion'
import { Key, Copy, Check, AlertTriangle, X } from 'lucide-react'

interface CreateApiKeyModalProps {
  onClose: () => void
  onCreate: (data: { name: string; scopes: string[]; expires_days: number | null }) => Promise<{ key: string } | void>
  isLoading: boolean
}

const AVAILABLE_SCOPES = [
  { value: 'read', label: 'Read', description: 'Read profiles and personas' },
  { value: 'write', label: 'Write', description: 'Create and update profiles' },
  { value: 'distill', label: 'Distill', description: 'Submit transcripts for distillation' },
]

const EXPIRATION_OPTIONS = [
  { value: null, label: 'Never' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
]

export default function CreateApiKeyModal({ onClose, onCreate, isLoading }: CreateApiKeyModalProps) {
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>(['read', 'write', 'distill'])
  const [expiresDays, setExpiresDays] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [createdKey, setCreatedKey] = useState('')
  const [copied, setCopied] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!name.trim()) {
      setError('Key name is required')
      return
    }

    if (name.trim().length < 3) {
      setError('Key name must be at least 3 characters')
      return
    }

    if (scopes.length === 0) {
      setError('Select at least one scope')
      return
    }

    try {
      const result = await onCreate({ name: name.trim(), scopes, expires_days: expiresDays })
      if (result?.key) {
        setCreatedKey(result.key)
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create API key')
    }
  }

  const toggleScope = (scope: string) => {
    setScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    )
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(createdKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = createdKey
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Show-once key reveal dialog
  if (createdKey) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-sage-50 flex items-center justify-center">
              <Key className="w-5 h-5 text-sage-600" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-midnight-800">API Key Created</h3>
              <p className="text-xs text-warm-500">{name}</p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800">
              This key will not be shown again. Copy it now and store it securely.
            </p>
          </div>

          <div className="bg-warm-50 border border-warm-200 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between gap-2">
              <code className="text-sm text-midnight-800 break-all font-mono">{createdKey}</code>
              <button
                onClick={handleCopy}
                className="shrink-0 p-2 rounded-lg hover:bg-warm-100 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-sage-600" />
                ) : (
                  <Copy className="w-4 h-4 text-warm-500" />
                )}
              </button>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full px-4 py-2 rounded-lg bg-copper-500 text-white text-sm font-medium hover:bg-copper-600 transition-colors"
          >
            Done
          </button>
        </motion.div>
      </div>
    )
  }

  // Create key form
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-midnight-800">Create API Key</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-warm-100 transition-colors">
            <X className="w-4 h-4 text-warm-500" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-1">
              Key Name
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., my-service-prod"
              className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm focus:outline-none focus:ring-2 focus:ring-copper-500/20 focus:border-copper-500"
              autoFocus
            />
            <p className="mt-1 text-xs text-warm-400">
              Name this key for the service that will use it
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Scopes
            </label>
            <div className="space-y-2">
              {AVAILABLE_SCOPES.map(scope => (
                <label
                  key={scope.value}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-warm-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope.value)}
                    onChange={() => toggleScope(scope.value)}
                    className="w-4 h-4 rounded border-warm-300 text-copper-500 focus:ring-copper-500/20"
                  />
                  <div>
                    <span className="text-sm font-medium text-midnight-700">{scope.label}</span>
                    <p className="text-xs text-warm-400">{scope.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-1">
              Expiration
            </label>
            <select
              value={expiresDays ?? ''}
              onChange={e => setExpiresDays(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 rounded-lg border border-warm-200 text-sm focus:outline-none focus:ring-2 focus:ring-copper-500/20 focus:border-copper-500"
            >
              {EXPIRATION_OPTIONS.map(opt => (
                <option key={opt.label} value={opt.value ?? ''}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-warm-200 text-sm font-medium text-warm-600 hover:bg-warm-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 rounded-lg bg-copper-500 text-white text-sm font-medium hover:bg-copper-600 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Creating...' : 'Create Key'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}
